import LayerSearchSource from "@arcgis/core/widgets/Search/LayerSearchSource.js";
import SearchVM from "@arcgis/core/widgets/Search/SearchViewModel.js";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppConfig } from "../../AppConfig";

import MagnifyingGlassIcon from "../../assets/header/magnifyingglass.svg?react";

import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { LayerFieldsIB } from "../../constants/LayerFields";
import { JaNei } from "../../constants/nfsDomains";
import { LeftPanelTabs } from "../../constants/Panels";
import { AppContext } from "../../Context";
import { setLeftPanelActiveTab } from "../../store/appSlice";
import { StoreState } from "../../store/rootReducer";
import { ItemDisplayType, setSearchItemDisplayType, setSearchResultsLoading } from "../../store/searchSlice";
import { getMapView } from "../../utils/arcgisUtils";
import "./Search.css";

const Search = () => {
    const dispatch = useDispatch();

    const [searchTerm, setSearchTerm] = useState<string>("");

    const searchBarRef = useRef<HTMLInputElement | null>(null);
    const searchRef = useRef<SearchVM | null>(null);

    const appContext = useContext(AppContext);
    const setSearchResults = appContext!.searchResults.set;

    const activeVersionGuid = useSelector((state: StoreState) => state.version.activeGuid);

    const initSearch = () => {
        const mapView = getMapView();

        if (mapView?.map) {
            const sources: LayerSearchSource[] = [];

            for (const configLayer of AppConfig.Layers) {
                const layer = mapView.map.findLayerById(configLayer.Name);
                if (layer) {
                    if (configLayer.SearchFields.length === 0) continue;
                    const source = new LayerSearchSource({
                        layer: layer,
                        searchFields: configLayer.SearchFields,
                        outFields: ["*"],
                        exactMatch: false,
                        // prefix: "%", // Use prefix for wildcard search. Disabled for now, caused problems with searching in number fields
                    });
                    if (configLayer.Name === "IB") {
                        // Exclude IB linked to FL/LA, they are handled through their main feature
                        source.filter = {
                            where: `${LayerFieldsIB.tilknyttet_fl_la} <> ${JaNei.Ja} OR ${LayerFieldsIB.tilknyttet_fl_la} IS NULL`,
                        };
                    }
                    sources.push(source);
                }
            }

            searchRef.current = new SearchVM({
                view: mapView,
                maxResults: 10000,
                minSuggestCharacters: 3,
                sources: sources,
                includeDefaultSources: false,
                autoSelect: false,
                goToOverride: async () => {},
            });
        } else {
            setTimeout(() => {
                initSearch();
            }, 500);
        }
    };

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setSearchItemDisplayType(ItemDisplayType.Search));
        setSearchResults([]);
        dispatch(setSearchResultsLoading(true));
        setSearchTerm(event.target.value);
    };

    // Create stable reference to debounced function
    const debounceSearch = useMemo(
        () =>
            _.debounce((text: string) => {
                if (searchRef.current) {
                    searchRef.current.search(text).then((results) => {
                        if (!results) return;
                        setSearchResults(results.results);
                        dispatch(setLeftPanelActiveTab(LeftPanelTabs.SEARCH));
                        dispatch(setSearchResultsLoading(false));
                    });
                }
            }, 500),
        [setSearchResults, searchRef]
    );

    useEffect(() => {
        if (searchTerm.length > 2) {
            debounceSearch(searchTerm);
        } else if (searchRef.current) {
            searchRef.current.clear();
            setSearchResults([]);
        }

        // Clean up debounce on unmount
        return () => {
            debounceSearch.cancel();
        };
    }, [searchTerm, debounceSearch]);

    useEffect(() => {
        setSearchTerm("");
    }, [activeVersionGuid]);

    useEffect(() => {
        initSearch();

        return () => {
            searchRef.current?.destroy();
        };
    }, []);

    return (
        <div ref={searchBarRef} className="search-container">
            <div className="search-input-box">
                <input
                    className="search-input"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Søk i kartet"
                />
                <MagnifyingGlassIcon />
            </div>
        </div>
    );
};

export default Search;
